import { createQueryFn } from '@oneview/redux/utils';
import { formatOptionExpirations } from '@oneview/utils';
import { OrderFilterType, OverExposedType, PositionType, TopPositionsType } from '@orbis-systems/orbis-api-middleware';
import { Currency } from '@orbis-systems/orbis-react-toolkit';
import { api } from './baseApi';

export const branchApi = api.injectEndpoints({
    endpoints: (build) => ({
        getBranchInfo: build.query({
            queryFn: createQueryFn((_: void, { extra: api }) => api.orbisMiddleware.branch.getInfo()),
        }),
        getBranchPortfolio: build.query({
            queryFn: createQueryFn(
                (
                    params:
                        | { currency?: string; combined?: boolean; symbol?: string; positionType?: PositionType }
                        | undefined,
                    { extra: api },
                ) => {
                    const { combined = true, symbol, positionType, currency = Currency.UsDollar } = params || {};
                    return api.orbisMiddleware.branch.getPortfolio({ combined, currency, symbol, positionType });
                },
            ),
        }),
        getBranchOrders: build.query({
            queryFn: createQueryFn(
                (
                    params: { orderFilter?: OrderFilterType | OrderFilterType[]; symbol?: string } | undefined,
                    { extra: api },
                ) => {
                    const { orderFilter, symbol } = params || {};
                    return api.orbisMiddleware.branch.getOrders(
                        orderFilter
                            ? orderFilter
                            : [OrderFilterType.Latest, OrderFilterType.Open, OrderFilterType.Today],
                        symbol,
                    );
                },
            ),
        }),
        getBranchAccountTypesBreakdown: build.query({
            queryFn: createQueryFn((_: void, { extra: api }) =>
                api.orbisMiddleware.stat.getBranchAccountTypesBreakdownStat(),
            ),
        }),
        getBranchMarketCapBreakdown: build.query({
            queryFn: createQueryFn((_: void, { extra: api }) =>
                api.orbisMiddleware.stat.getBranchMarketCapBreakdownStat(),
            ),
        }),
        getBranchSectorsBreakdown: build.query({
            queryFn: createQueryFn((_: void, { extra: api }) =>
                api.orbisMiddleware.stat.getBranchSectorsBreakdownStat(),
            ),
        }),
        getBranchAccountSizesBreakdown: build.query({
            queryFn: createQueryFn((_: void, { extra: api }) =>
                api.orbisMiddleware.stat.getBranchAccountSizesBreakdownStat(),
            ),
        }),
        getBranchTopPositionsBreakdown: build.query({
            queryFn: createQueryFn((by: TopPositionsType = TopPositionsType.Weight, { extra: api }) =>
                api.orbisMiddleware.stat.getBranchTopPositionsStat(by),
            ),
        }),
        getBranchOverExposedAccounts: build.query({
            queryFn: createQueryFn(
                ({ threshold = 0, type }: { threshold?: number; type: OverExposedType }, { extra: api }) =>
                    api.orbisMiddleware.branch.getOverExposedAccounts(threshold, type),
            ),
        }),
        getPositionAccounts: build.query({
            queryFn: createQueryFn((symbol: string, { extra: api }) =>
                api.orbisMiddleware.branch.getPositionAccounts(symbol),
            ),
        }),
        getBranchAcatTransfers: build.query({
            queryFn: createQueryFn((_: void, { extra: api }) => api.orbisMiddleware.branch.getBranchAcatTransfer()),
        }),
        getBranchOptionExpirations: build.query({
            queryFn: createQueryFn(async (_: void, { extra: api }) => {
                interface OptionExpirationUpcoming {
                    maturity: Date;
                    longQty: number;
                    shortQty: number;
                }

                const upcomingExpirations: OptionExpirationUpcoming[] = (
                    await api.orbisSdk.get('v1/branch/portfolio/options/upcoming')
                ).data;
                const data = await Promise.all(
                    upcomingExpirations.map((item: OptionExpirationUpcoming) => {
                        return api.orbisSdk.get(
                            'v1/branch/portfolio/options/upcoming/details',
                            {},
                            { params: { maturity: item.maturity, loadQuotes: true } },
                        );
                    }),
                );
                return formatOptionExpirations(data.length > 0 ? data[0]!.data : []);
            }),
        }),
    }),
});

export const {
    useLazyGetBranchPortfolioQuery,
    useGetBranchAccountTypesBreakdownQuery,
    useGetBranchMarketCapBreakdownQuery,
    useGetBranchSectorsBreakdownQuery,
    useGetBranchAccountSizesBreakdownQuery,
    useGetBranchTopPositionsBreakdownQuery,
    useGetBranchOverExposedAccountsQuery,
    useGetBranchOptionExpirationsQuery,
    useLazyGetBranchOptionExpirationsQuery,
    useGetPositionAccountsQuery,
    useGetBranchOrdersQuery,
    useGetBranchAcatTransfersQuery,
} = branchApi;
